import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { isOmbudsman } from '../../../utils/rbac';
import UserProfile from '../user/UserProfile';

interface NavItem {
  name: string;
  path: string;
  icon?: React.ReactNode;
  badge?: number;
}

interface NavSection {
  name: string;
  icon: React.ReactNode;
  items: NavItem[];
}

const GO_LAB_ICON = (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24">
    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.98" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
  </svg>
);

const GO_LAB_ITEMS: NavItem[] = [
  { name: 'Taxonomy', path: '/v4/go-lab/intelligence/taxonomy' },
  { name: 'Prompts', path: '/v4/go-lab/intelligence/prompts' },
];

const V3Sidebar: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isGoLabExpanded, setIsGoLabExpanded] = useState(false);
  const { user, logout } = useAuth();
  const isAdmin = user?.role?.toLowerCase?.() === 'admin';
  const isOmbudsmanUser = isOmbudsman(user);
  const navigate = useNavigate();
  const location = useLocation();

  // Auto-expand GO-Lab section when on a GO-Lab route
  const isOnGoLabRoute = location.pathname.startsWith('/v4/go-lab');
  const goLabExpanded = isGoLabExpanded || isOnGoLabRoute;
  const baseNavItems: NavItem[] = [
    {
      name: 'Today',
      path: '/v4/today',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 23 18">
          <g clipPath="url(#clip0_2005_69)">
            <g>
              <path
                d="M8.60686 0.902736V3.36474"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.98"
              />
            </g>
            <g>
              <path
                d="M18.1841 0.902736V3.36474"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.98"
              />
            </g>
            <g>
              <path
                d="M13.3774 0.902736V3.36474"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.98"
              />
            </g>
            <g>
              <path
                d="M4.78864 6.04559C4.78864 11.4985 0.897871 13.9787 0.897871 13.9787H17.8214C17.8214 13.9787 21.7121 11.4985 21.7121 6.04559"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.98"
              />
            </g>
            <path
              d="M4.68888 14.845V15.1915C4.68888 16.2401 5.53233 17.0973 6.57532 17.0973H20.2248C21.2677 17.0973 22.1112 16.2401 22.1112 15.1915V3.76596C22.1112 2.71733 21.2677 1.86018 20.2248 1.86018H6.57532C5.53233 1.86018 4.68888 2.71733 4.68888 3.76596V5.94529H21.7484"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.98"
            />
          </g>
          <defs>
            <clipPath id="clip0_2005_69">
              <rect fill="white" height="18" width="23" />
            </clipPath>
          </defs>
        </svg>
      )
    },
    {
      name: 'Monitor',
      path: '/v4/records',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24">
          <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.98" d="M20 7h-9"/>
          <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.98" d="M14 17H5"/>
          <circle cx="7" cy="7" r="3" stroke="currentColor" strokeWidth="1.98" fill="none"/>
          <circle cx="17" cy="17" r="3" stroke="currentColor" strokeWidth="1.98" fill="none"/>
        </svg>
      ),
      badge: undefined
    },
    {
      name: 'Case Guidance',
      path: '/v3/ombudsman',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24">
          <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.98" d="M3 12h18M12 3v18M8 8l8 8M16 8l-8 8"/>
          <circle cx="9" cy="9" r="1.5" fill="currentColor"/>
          <circle cx="15" cy="15" r="1.5" fill="currentColor"/>
        </svg>
      )
    },
    {
      name: 'Reports',
      path: '/v4/reports',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24">
          <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.98" d="M9 17H7a2 2 0 01-2-2V5a2 2 0 012-2h10a2 2 0 012 2v1"/>
          <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.98" d="M13 9h6a2 2 0 012 2v8a2 2 0 01-2 2h-6a2 2 0 01-2-2v-8a2 2 0 012-2z"/>
          <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.98" d="M14 13h4m-4 3h2"/>
        </svg>
      )
    },
    // Admin-only items below
    {
      name: 'Evaluate',
      path: '/v4/evaluate',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24">
          <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.98" d="M9 11l3 3L22 4"/>
          <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.98" d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
        </svg>
      )
    },
    {
      name: 'Taxonomy',
      path: '/v3/taxonomy',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24">
          <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.98" d="M3 3v18h18M5 17l4-7 4 7m4-7v7"/>
        </svg>
      )
    },
    {
      name: 'Settings',
      path: '/v3/settings',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 22 23">
          <g clipPath="url(#clip0_settings)">
            <path
              d="M10.706 7.69C8.85603 7.69 7.35603 9.19 7.35603 11.04C7.35603 12.89 8.85603 14.39 10.706 14.39C12.556 14.39 14.056 12.89 14.056 11.04C14.056 9.19 12.556 7.69 10.706 7.69ZM20.116 8.65L19.206 9.4C18.166 10.25 18.166 11.84 19.206 12.69L20.116 13.44C20.436 13.7 20.516 14.16 20.316 14.52L18.526 17.62C18.316 17.98 17.876 18.14 17.486 17.99L16.386 17.58C15.126 17.11 13.756 17.9 13.536 19.22L13.346 20.38C13.276 20.79 12.926 21.09 12.506 21.09H8.92603C8.50603 21.09 8.15603 20.79 8.08603 20.38L7.89603 19.22C7.67603 17.9 6.30603 17.1 5.04603 17.58L3.94603 17.99C3.55603 18.14 3.11603 17.98 2.90603 17.62L1.11603 14.52C0.906025 14.16 0.986025 13.7 1.31603 13.44L2.22603 12.69C3.26603 11.84 3.26603 10.25 2.22603 9.4L1.31603 8.65C0.996025 8.39 0.916025 7.93 1.11603 7.57L2.90603 4.47C3.11603 4.11 3.55603 3.95 3.94603 4.1L5.04603 4.51C6.30603 4.98 7.67603 4.19 7.89603 2.87L8.08603 1.71C8.15603 1.3 8.50603 1 8.92603 1H12.506C12.926 1 13.276 1.3 13.346 1.71L13.536 2.87C13.756 4.19 15.126 4.99 16.386 4.51L17.486 4.1C17.876 3.95 18.316 4.11 18.526 4.47L20.316 7.57C20.526 7.93 20.446 8.39 20.116 8.65Z"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.98"
            />
          </g>
        </svg>
      )
    }
  ];

  // Filter nav items based on role
  let navItems: NavItem[] = baseNavItems;
  
  if (!isAdmin) {
    navItems = navItems.filter(item => !['Settings', 'Taxonomy', 'Evaluate'].includes(item.name));
  }
  
  if (!isOmbudsmanUser) {
    navItems = navItems.filter(item => !['Case Guidance', 'Reports'].includes(item.name));
  }

  return (
    <div className={`${isCollapsed ? 'w-16' : 'w-56'} bg-white flex flex-col border-r border-gray-200 transition-all duration-300 relative`}>
      {/* Logo */}
      <div className="h-20 flex items-center justify-center px-4">
        <svg className="w-[62px] h-[62px]" fill="none" viewBox="0 0 62 62">
          <g clipPath="url(#clip0_2008_67)">
            <path
              d="M47.5807 0H14.4193C6.45575 0 0 6.45575 0 14.4193V47.5807C0 55.5443 6.45575 62 14.4193 62H47.5807C55.5443 62 62 55.5443 62 47.5807V14.4193C62 6.45575 55.5443 0 47.5807 0Z"
              fill="#4342FF"
            />
            <path
              d="M30.428 50.4676C25.7751 50.4676 21.199 48.7554 17.6748 45.5306C17.0529 44.9625 17.0068 43.9874 17.575 43.3654C18.1432 42.7435 19.1183 42.6975 19.7402 43.2656C23.9247 47.0893 29.8828 48.3792 35.2805 46.6363C36.0867 46.3752 36.9543 46.8206 37.2077 47.6267C37.4687 48.4329 37.0234 49.2929 36.2172 49.5539C34.3207 50.1682 32.3628 50.4676 30.4203 50.4676H30.428Z"
              fill="white"
            />
            <path
              d="M30.4894 41.1695C27.1264 41.1695 23.8249 39.9871 21.2067 37.7759C16.001 33.3533 14.5268 26.804 17.3523 20.6846C19.8477 15.2946 25.9747 10.4421 34.0367 11.7704C37.0004 12.2541 40.133 13.6515 42.8741 15.6939C43.519 16.1776 44.7475 17.3063 45.4232 17.9359C45.7917 18.2814 46.0144 18.7728 46.022 19.2795C46.0297 19.7863 45.8301 20.293 45.4692 20.6539L43.4806 22.6502C44.0027 24.6157 44.8627 28.8463 43.6419 32.1632C42.0679 36.4245 38.6128 39.5418 34.1595 40.7089C32.9464 41.0237 31.7179 41.1849 30.4971 41.1849L30.4894 41.1695ZM23.1953 35.4341C25.9978 37.8142 29.8061 38.6665 33.3763 37.7298C36.8545 36.8161 39.5418 34.3975 40.7626 31.0883C41.6072 28.7926 41.0697 25.2069 40.348 22.8421C40.1484 22.1741 40.325 21.4524 40.824 20.961L42.4364 19.3486C41.7991 18.7728 41.3077 18.3428 41.039 18.1432C38.7279 16.4156 35.9945 15.1948 33.5453 14.7879C27.0727 13.7283 22.1511 17.6287 20.1394 21.9668C18.3889 25.7444 18.2814 31.2649 23.1876 35.4341H23.1953Z"
              fill="white"
            />
            <path
              d="M43.1198 46.5134C44.7396 46.5134 46.0528 45.2003 46.0528 43.5804C46.0528 41.9606 44.7396 40.6474 43.1198 40.6474C41.4999 40.6474 40.1867 41.9606 40.1867 43.5804C40.1867 45.2003 41.4999 46.5134 43.1198 46.5134Z"
              fill="#97FAFF"
            />
          </g>
          <defs>
            <clipPath id="clip0_2008_67">
              <rect fill="white" height="62" width="62" />
            </clipPath>
          </defs>
        </svg>
      </div>

      {/* Collapse Toggle */}
      <div className="px-2 mb-2">
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="w-full flex items-center justify-center p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
          aria-label="Toggle sidebar"
          title="Toggle sidebar"
        >
          <svg className={`w-4 h-4 transition-transform ${isCollapsed ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24">
            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
          </svg>
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 py-4 space-y-1">
        {/* Regular nav items (except Settings, which goes after GO-Lab) */}
        {navItems.filter(item => item.name !== 'Settings').map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              `group flex items-center ${isCollapsed ? 'justify-center px-2' : 'px-4'} py-3 text-base font-semibold rounded-lg transition-colors ${
                isActive
                  ? 'bg-gray-100 text-blue-600'
                  : 'text-gray-800 hover:bg-gray-50 hover:text-gray-900'
              }`
            }
            title={isCollapsed ? item.name : undefined}
          >
            {item.icon && (
              <span className={`flex-shrink-0 text-gray-600 ${!isCollapsed ? 'mr-4' : ''}`}>
                {item.icon}
              </span>
            )}
            {!isCollapsed && (
              <>
                <span className="flex-1">
                  {item.name === 'Ask GO' ? (
                    <>
                      Ask <span className="text-blue-600">GO</span>
                    </>
                  ) : (
                    item.name
                  )}
                </span>
                {item.badge && (
                  <span className="ml-auto bg-red-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {item.badge}
                  </span>
                )}
              </>
            )}
            {isCollapsed && item.badge && (
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-600 rounded-full"></span>
            )}
          </NavLink>
        ))}

        {/* GO-Lab expandable section (admin only) */}
        {isAdmin && (
          <div>
            <button
              onClick={() => {
                if (isCollapsed) {
                  setIsCollapsed(false);
                  setIsGoLabExpanded(true);
                } else {
                  setIsGoLabExpanded(!goLabExpanded);
                }
              }}
              className={`w-full group flex items-center ${isCollapsed ? 'justify-center px-2' : 'px-4'} py-3 text-base font-semibold rounded-lg transition-colors ${
                isOnGoLabRoute
                  ? 'bg-gray-100 text-blue-600'
                  : 'text-gray-800 hover:bg-gray-50 hover:text-gray-900'
              }`}
              title={isCollapsed ? 'GO-Lab' : undefined}
            >
              <span className={`flex-shrink-0 text-gray-600 ${!isCollapsed ? 'mr-4' : ''}`}>
                {GO_LAB_ICON}
              </span>
              {!isCollapsed && (
                <>
                  <span className="flex-1 text-left">
                    GO-<span className="text-blue-600">Lab</span>
                  </span>
                  <svg
                    className={`w-4 h-4 text-gray-400 transition-transform ${goLabExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                  </svg>
                </>
              )}
            </button>
            {goLabExpanded && !isCollapsed && (
              <div className="ml-6 mt-1 space-y-1">
                {GO_LAB_ITEMS.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) =>
                      `group flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                        isActive
                          ? 'bg-gray-100 text-blue-600'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`
                    }
                  >
                    {item.name}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Settings (always last) */}
        {navItems.filter(item => item.name === 'Settings').map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              `group flex items-center ${isCollapsed ? 'justify-center px-2' : 'px-4'} py-3 text-base font-semibold rounded-lg transition-colors ${
                isActive
                  ? 'bg-gray-100 text-blue-600'
                  : 'text-gray-800 hover:bg-gray-50 hover:text-gray-900'
              }`
            }
            title={isCollapsed ? item.name : undefined}
          >
            {item.icon && (
              <span className={`flex-shrink-0 text-gray-600 ${!isCollapsed ? 'mr-4' : ''}`}>
                {item.icon}
              </span>
            )}
            {!isCollapsed && <span className="flex-1">{item.name}</span>}
          </NavLink>
        ))}
      </nav>
      
      {/* User Profile & Logout */}
      <div className="mt-auto border-t border-gray-200 py-3 px-4">
        <div className={`flex ${isCollapsed ? 'justify-center' : ''}`}>
          <UserProfile isCollapsed={isCollapsed} />
        </div>
      </div>
    </div>
  );
};

export default V3Sidebar;
